import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {QRContentInputOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/QR-content-input-option';
import {QRBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/QR-background-option';
import {QRContentColorFill} from '@Components/poster-editor/components/poster-editing-side-panel/components/QR-content-color-fill';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {QrForegroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/qr-foreground-color-panel';
import {QrBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/qr-background-color-panel';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {QRCodeItemPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-QR-options/poster-editing-QR-options.types';
import {useIsOpenedQRPanelValid} from '@Hooks/poster-editor/useIsOpenedQRPanelValid';
import {AnimationPanelType} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-animation-option/default-animation-options.types';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemIntroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-intro-animation-panel/item-intro-animation-panel';
import {ItemOutroAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-outro-animation-panel/item-outro-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingQROptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(QRCodeItemPanels.NONE);
  useIsOpenedQRPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error(`Active item is null in QR Options`);
  }

  if (!activeItem?.isQRItem()) {
    throw new Error(`Expected active item type to be QR, instead got ${activeItem?.gitype}`);
  }

  const isSubPanelOpened = (): boolean => {
    return !(currentlyOpenedPanel === QRCodeItemPanels.NONE);
  };

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === QRCodeItemPanels.INTRO_ANIMATION) {
      return window.i18next.t('pmwjs_entrance_animation');
    } else if (currentlyOpenedPanel === QRCodeItemPanels.OUTRO_ANIMATION) {
      return window.i18next.t('pmwjs_exit_animation');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeaderContent = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return (
        <ContextualMenuHeader id="qrcode-contextual-menu-header">
          <ContextualMenuTextHeader
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }
              setCurrentlyOpenedPanel(QRCodeItemPanels.NONE);
            }}
            text={getHeaderText()}
          />
        </ContextualMenuHeader>
      );
    }

    return null;
  };

  const getHeader = (): ReactElement<any> | null => {
    return getHeaderContent();
  };

  const getSubPanels = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case QRCodeItemPanels.COLOR_FILL:
        return <QrForegroundColorPanel />;
      case QRCodeItemPanels.BACKGROUND:
        return <QrBackgroundColorPanel />;
      case QRCodeItemPanels.INTRO_ANIMATION:
        return <ItemIntroAnimationPanel />;
      case QRCodeItemPanels.OUTRO_ANIMATION:
        return <ItemOutroAnimationPanel />;
      default:
        throw new Error(`unhandled sub panel type ${currentlyOpenedPanel} for QR Item`);
    }
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(type: AnimationPanelType): void => {
            setCurrentlyOpenedPanel(type === AnimationPanelType.INTRO_ANIMATION_STYLES ? QRCodeItemPanels.INTRO_ANIMATION : QRCodeItemPanels.OUTRO_ANIMATION);
          }}
        />
        <QRContentInputOption />
        <ContextualMenuHeadingOption id="qr-style-heading-option" text={window.i18next.t('pmwjs_style')} />
        <QRContentColorFill
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(QRCodeItemPanels.COLOR_FILL);
          }}
        />
        <QRBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(QRCodeItemPanels.BACKGROUND);
          }}
        />
      </>
    );
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return <ItemPositionContextualOptions />;
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };
  const getOptions = (): ReactElement<any> => {
    if (!isSubPanelOpened()) {
      return getDefaultOptions();
    }

    return getSubPanels();
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.QR_DEFAULT_POSITION : ContextualMenuContentKeys.QR_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu contentKey={getKey()} id="qr-contextual-options" includeLeftRightPadding={!isSubPanelOpened()} header={getHeader()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
